import React from 'react';
import DisModal from './design-system/DisModal';
import DisButton from './design-system/DisButton';

const PlanLimitReachedModal = ({ 
  isOpen, 
  onClose, 
  subscription, 
  currentPlan, 
  storiesThisMonth, 
  maxStories,
  onNavigateToSubscription,
  onPurchaseStoryPack 
}) => {
  if (!isOpen || !subscription || !currentPlan) return null;

  const isTrialUser = subscription.status === 'trial' && (currentPlan.id === 'free' || currentPlan.id === 'free_trial');
  const isEssentialUser = currentPlan.id === 'essential_monthly' || currentPlan.id === 'essential_yearly';
  const isFamilyUser = currentPlan.id === 'family_monthly' || currentPlan.id === 'family_yearly';

  // Calcul de la date de renouvellement mensuel
  const getNextRenewalDate = () => {
    if (!subscription.current_period_end) return 'Inconnue';
    
    const renewalDate = new Date(subscription.current_period_end);
    return renewalDate.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const renderTrialContent = () => (
    <>
      <p className="mb-6 text-gray-600">
        Vous avez utilisé votre {maxStories} histoire gratuite ce mois-ci. 
        Pour continuer à créer des histoires magiques, choisissez l'un de nos plans premium :
      </p>

      <div className="space-y-4 mb-6">
        {/* Plan Essential */}
        <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-semibold text-blue-800">Plan Essential</h4>
            <span className="text-blue-600 font-bold">9,90€/mois</span>
          </div>
          <p className="text-sm text-gray-600 mb-3">✨ 2 histoires par mois + toutes les fonctionnalités</p>
          <DisButton
            onClick={() => onNavigateToSubscription('essential_monthly')}
            variant="primary"
            size="small"
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            Choisir Essential
          </DisButton>
        </div>

        {/* Plan Family */}
        <div className="border border-teal-200 rounded-lg p-4 bg-teal-50">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-semibold text-teal-800">Plan Family</h4>
            <span className="text-teal-600 font-bold">15,90€/mois</span>
          </div>
          <p className="text-sm text-gray-600 mb-3">🌟 5 histoires par mois + conversations illimitées</p>
          <DisButton
            onClick={() => onNavigateToSubscription('family_monthly')}
            variant="primary"
            size="small"
            className="w-full bg-teal-600 hover:bg-teal-700"
          >
            Choisir Family
          </DisButton>
        </div>
      </div>
    </>
  );

  const renderEssentialContent = () => (
    <>
      <p className="mb-6 text-gray-600">
        Vous avez utilisé vos {maxStories} histoires du mois ({storiesThisMonth}/{maxStories}). 
        Voici vos options pour continuer :
      </p>

      <div className="space-y-4 mb-6">
        {/* Attendre le renouvellement */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-2">🗓️ Attendre le renouvellement</h4>
          <p className="text-sm text-gray-600 mb-3">
            Votre quota se renouvelle le <strong>{getNextRenewalDate()}</strong>
          </p>
        </div>

        {/* Upgrade vers Family */}
        <div className="border border-teal-200 rounded-lg p-4 bg-teal-50">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-semibold text-teal-800">⬆️ Passer au plan Family</h4>
            <span className="text-teal-600 font-bold">15,90€/mois</span>
          </div>
          <p className="text-sm text-gray-600 mb-3">5 histoires par mois + conversations illimitées</p>
          <DisButton
            onClick={() => onNavigateToSubscription('family_monthly')}
            variant="primary"
            size="small"
            className="w-full bg-teal-600 hover:bg-teal-700"
          >
            Upgrader vers Family
          </DisButton>
        </div>

        {/* Packs d'histoires */}
        <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
          <h4 className="font-semibold text-orange-800 mb-3">📚 Acheter des histoires supplémentaires</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-orange-300 rounded p-3 bg-white">
              <div className="text-center mb-2">
                <span className="font-bold text-orange-600">5 histoires</span>
                <br />
                <span className="text-lg font-bold">4,99€</span>
              </div>
              <DisButton
                onClick={() => onPurchaseStoryPack('price_1SFIzjQPJb5o0TOCgMJuiXSm', 5)}
                variant="outline"
                size="small"
                className="w-full border-orange-400 text-orange-600 hover:bg-orange-100"
              >
                Acheter
              </DisButton>
            </div>
            
            <div className="border border-orange-300 rounded p-3 bg-white relative">
              <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                +2 offertes!
              </div>
              <div className="text-center mb-2">
                <span className="font-bold text-orange-600">12 histoires</span>
                <br />
                <span className="text-sm text-gray-500 line-through">11,99€</span>
                <br />
                <span className="text-lg font-bold text-green-600">9,99€</span>
              </div>
              <DisButton
                onClick={() => onPurchaseStoryPack('price_1SFJ1YQPJb5o0TOCkbHqRisI', 12)}
                variant="outline"
                size="small"
                className="w-full border-green-400 text-green-600 hover:bg-green-100"
              >
                Meilleure offre!
              </DisButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderFamilyContent = () => (
    <>
      <p className="mb-6 text-gray-600">
        Vous avez utilisé vos {maxStories} histoires du mois ({storiesThisMonth}/{maxStories}). 
        Voici vos options pour continuer :
      </p>

      <div className="space-y-4 mb-6">
        {/* Attendre le renouvellement */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-2">🗓️ Attendre le renouvellement</h4>
          <p className="text-sm text-gray-600 mb-3">
            Votre quota se renouvelle le <strong>{getNextRenewalDate()}</strong>
          </p>
        </div>

        {/* Packs d'histoires */}
        <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
          <h4 className="font-semibold text-orange-800 mb-3">📚 Acheter des histoires supplémentaires</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-orange-300 rounded p-3 bg-white">
              <div className="text-center mb-2">
                <span className="font-bold text-orange-600">5 histoires</span>
                <br />
                <span className="text-lg font-bold">4,99€</span>
              </div>
              <DisButton
                onClick={() => onPurchaseStoryPack('price_1SFIzjQPJb5o0TOCgMJuiXSm', 5)}
                variant="outline"
                size="small"
                className="w-full border-orange-400 text-orange-600 hover:bg-orange-100"
              >
                Acheter
              </DisButton>
            </div>
            
            <div className="border border-orange-300 rounded p-3 bg-white relative">
              <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                +2 offertes!
              </div>
              <div className="text-center mb-2">
                <span className="font-bold text-orange-600">12 histoires</span>
                <br />
                <span className="text-sm text-gray-500 line-through">11,99€</span>
                <br />
                <span className="text-lg font-bold text-green-600">9,99€</span>
              </div>
              <DisButton
                onClick={() => onPurchaseStoryPack('price_1SFJ1YQPJb5o0TOCkbHqRisI', 12)}
                variant="outline"
                size="small"
                className="w-full border-green-400 text-green-600 hover:bg-green-100"
              >
                Meilleure offre!
              </DisButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const getTitle = () => {
    if (isTrialUser) {
      return '🎭 Limite d\'histoires atteinte';
    } else if (isEssentialUser) {
      return '📚 Quota Essential épuisé';
    } else if (isFamilyUser) {
      return '🌟 Quota Family épuisé';
    }
    return 'Limite d\'histoires atteinte';
  };

  const renderContent = () => {
    if (isTrialUser) {
      return renderTrialContent();
    } else if (isEssentialUser) {
      return renderEssentialContent();
    } else if (isFamilyUser) {
      return renderFamilyContent();
    }
    return null;
  };

  return (
    <DisModal
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      size="large"
    >
      {renderContent()}

      <div className="flex justify-end pt-4 border-t border-gray-200">
        <DisButton
          onClick={onClose}
          variant="outline"
          className="text-gray-600 border-gray-300 hover:bg-gray-50"
        >
          Fermer
        </DisButton>
      </div>
    </DisModal>
  );
};

export default PlanLimitReachedModal;